export const TECHNICAL_SECURITY_ACTION_OPTIONS = Object.freeze([
  { id: "executive_remediation_plan", label: "Executive Remediation Plan" },
  { id: "zero_false_positive_validation", label: "Zero False Positive Validation" },
  { id: "manual_validation_checklist", label: "Manual Validation Checklist" },
  { id: "business_risk_explanation", label: "Business Risk Explanation" },
  { id: "attack_path_simulation", label: "Attack Path Simulation" },
  { id: "patch_priority_decision", label: "Patch Priority Decision" },
  { id: "compensating_controls", label: "Compensating Controls" },
  { id: "incident_response_readiness", label: "Incident Response Readiness" },
  { id: "ioc_hunting_guide", label: "IOC Hunting Guide" },
  { id: "soc_investigation_guide", label: "SOC Investigation Guide" },
  { id: "compliance_impact", label: "Compliance Impact" },
  { id: "executive_email_draft", label: "Executive Email Draft" },
  { id: "change_management_plan", label: "Change Management Plan" },
  { id: "devops_fix_guide", label: "DevOps Fix Guide" },
  { id: "secure_configuration_review", label: "Secure Configuration Review" },
  { id: "exposure_assessment", label: "Exposure Assessment" },
  { id: "threat_intelligence_correlation", label: "Threat Intelligence Correlation" },
  { id: "ai_remediation_verification", label: "AI Remediation Verification" },
  { id: "root_cause_analysis", label: "Root Cause Analysis" },
  { id: "ciso_action_summary", label: "CISO Action Summary" },
]);

export const FINANCIAL_SECURITY_ACTION_OPTIONS = Object.freeze([
  { id: "financial_impact_analysis", label: "Financial Impact Analysis" },
  { id: "financial_cost_breakdown", label: "Cost Breakdown" },
  { id: "financial_roi_of_remediation", label: "ROI of Remediation" },
  { id: "financial_executive_business_case", label: "Executive Business Case" },
  { id: "financial_priority_ranking", label: "Prioritize by Financial Impact" },
  { id: "financial_downtime_estimation", label: "Downtime Estimation" },
  { id: "financial_ransomware_scenario", label: "Ransomware Scenario" },
  { id: "financial_board_level_report", label: "Board-Level Report" },
  { id: "financial_vendor_risk_cost", label: "Vendor Risk Cost" },
  { id: "financial_cyber_insurance_impact", label: "Cyber Insurance Impact" },
  { id: "financial_breach_probability", label: "Breach Probability" },
  { id: "financial_what_if_analysis", label: "What If Analysis" },
  { id: "financial_mttr_value", label: "MTTR Value" },
  { id: "financial_regulatory_cost", label: "Regulatory Cost" },
  { id: "financial_security_investment_recommendation", label: "Security Investment Recommendation" },
]);

export const WEB_FINDING_ACTION_OPTIONS = Object.freeze([
  { id: "finding_explain", label: "Explain This Finding" },
  { id: "finding_detection_reason", label: "Why Was This Detected?" },
  { id: "finding_manual_validation", label: "Manual Validation Steps" },
  { id: "finding_false_positive_assessment", label: "False-Positive Assessment" },
  { id: "finding_evidence_review", label: "Evidence Review" },
  { id: "finding_scope_assessment", label: "Affected URL and Scope" },
  { id: "finding_exploitation_scenario", label: "Exploitation Scenario" },
  { id: "finding_remediation_plan", label: "Step-by-Step Remediation" },
  { id: "finding_developer_fix_guide", label: "Developer Fix Guide" },
  { id: "finding_server_configuration_fix", label: "Server Configuration Fix" },
  { id: "finding_compensating_controls", label: "Temporary Compensating Controls" },
  { id: "finding_remediation_priority", label: "Remediation Priority" },
  { id: "finding_business_impact", label: "Business Impact" },
  { id: "finding_compliance_impact", label: "Compliance Impact" },
  { id: "finding_post_remediation_validation", label: "Post-Remediation Validation" },
  { id: "finding_retest_checklist", label: "Retesting Checklist" },
  { id: "finding_root_cause_analysis", label: "Root Cause Analysis" },
  { id: "finding_monitoring_recommendations", label: "Monitoring Recommendations" },
  { id: "finding_developer_ticket", label: "Create Developer Ticket" },
  { id: "finding_executive_summary", label: "Executive Summary" },
]);

export const CLOUD_FINDING_ACTION_OPTIONS = Object.freeze([
  { id: "cloud_full_analysis", label: "Full Cloud Risk Analysis" },
  { id: "cloud_detection_reason", label: "Why Was This Detected?" },
  { id: "cloud_business_technical_risk", label: "Business and Technical Risk" },
  { id: "cloud_mitre_mapping", label: "MITRE ATT&CK Mapping" },
  { id: "cloud_remediation_plan", label: "Step-by-Step Remediation" },
  { id: "cloud_terraform_fix", label: "Terraform Fix" },
  { id: "cloud_cli_fix", label: "Cloud CLI Fix" },
  { id: "cloud_score_impact", label: "SAVE Score Improvement" },
  { id: "cloud_related_findings", label: "Related Findings and Attack Path" },
  { id: "cloud_fix_effort", label: "Fix Time and Difficulty" },
]);

export const SECURITY_ACTION_CATEGORIES = Object.freeze([
  {
    id: "technical",
    label: "Technical Risk Estimation",
    description: "Validation, remediation, threat and compliance guidance",
    icon: "bi-tools",
    options: TECHNICAL_SECURITY_ACTION_OPTIONS,
  },
  {
    id: "financial",
    label: "Financial Risk Estimation",
    description: "Financial exposure, loss scenarios and investment decisions",
    icon: "bi-graph-up-arrow",
    options: FINANCIAL_SECURITY_ACTION_OPTIONS,
  },
]);

export const WEB_FINDING_ACTION_CATEGORIES = Object.freeze([
  {
    id: "web-finding",
    label: "Web Finding Analysis",
    description: "Validation, remediation and business guidance for this scanner finding",
    icon: "bi-shield-exclamation",
    options: WEB_FINDING_ACTION_OPTIONS,
  },
]);

export const CLOUD_FINDING_ACTION_CATEGORIES = Object.freeze([
  {
    id: "cloud-finding",
    label: "Cloud Finding Analysis",
    description: "Risk, IaC, CLI, attack-path, and remediation guidance grounded in this cloud finding",
    icon: "bi-cloud-check",
    options: CLOUD_FINDING_ACTION_OPTIONS,
  },
]);

export const hasVerifiedCve = (selection) =>
  !["finding", "network_finding"].includes(selection?.contextType) &&
  /^CVE-\d{4}-\d{4,}$/i.test(String(selection?.cveId || "").trim());

export const getSecurityActionCategories = (selection) =>
  selection?.contextType === "cloud_finding"
    ? CLOUD_FINDING_ACTION_CATEGORIES
    : selection?.contextType === "network_finding" || hasVerifiedCve(selection)
      ? SECURITY_ACTION_CATEGORIES
      : WEB_FINDING_ACTION_CATEGORIES;

export const getSecurityContextLabel = (selection) => {
  if (selection?.contextType === "cloud_finding") {
    return [
      selection?.provider,
      selection?.service,
      selection?.findingName || selection?.alert,
    ].filter(Boolean).join(" · ");
  }

  if (selection?.contextType === "network_finding") {
    const finding = selection?.findingName || selection?.alert || selection?.cveId;
    return [finding || "Selected network finding", selection?.port]
      .filter(Boolean)
      .join(" · ");
  }

  return (
    selection?.cveId ||
    selection?.alert ||
    selection?.findingName ||
    "Selected finding"
  );
};

export default TECHNICAL_SECURITY_ACTION_OPTIONS;
